"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const EPSILON = 1e-9;

type AttendancePlan =
  | { kind: "canMiss"; count: number }
  | { kind: "mustAttend"; count: number }
  | { kind: "unreachable" };

/**
 * With `attended` of `held` classes so far and a target ratio t:
 * - missing m more keeps attended / (held + m) ≥ t, so m ≤ attended / t − held;
 * - attending the next n straight gives (attended + n) / (held + n) ≥ t,
 *   so n ≥ (t × held − attended) / (1 − t).
 */
function planForTarget(
  attended: number,
  held: number,
  targetPercent: number
): AttendancePlan {
  const ratio = targetPercent / 100;
  if (attended / held + EPSILON >= ratio) {
    const canMiss = Math.floor(attended / ratio - held + EPSILON);
    return { kind: "canMiss", count: Math.max(0, canMiss) };
  }
  if (ratio >= 1) {
    return { kind: "unreachable" };
  }
  const mustAttend = Math.ceil(
    (ratio * held - attended) / (1 - ratio) - EPSILON
  );
  return { kind: "mustAttend", count: mustAttend };
}

/**
 * A number in the working, written the reader's way.
 *
 * Was `(Math.round(n * 100) / 100).toString()`, which is a rounding AND a formatting
 * decision in one expression — and the `toString` half always emits a period, so a
 * German reader saw `62 ÷ 80` beside an attendance figure already reading `77,5%`.
 * The formatter arrives as a parameter because this helper is module level and has no
 * locale in scope; two decimals is what the old rounding meant.
 */
function fmt(n: number, format: NumberFormatter): string {
  return format(n, { maximumFractionDigits: 2 });
}

type AttendanceStrings = Record<
  keyof typeof ATTENDANCE_CALCULATOR_STRINGS,
  string
>;

export function planMessage(
  plan: AttendancePlan,
  targetPercent: number,
  format: NumberFormatter,
  strings: AttendanceStrings,
  locale: string
): string {
  const target = fmt(targetPercent, format);
  if (plan.kind === "canMiss") {
    if (plan.count === 0) {
      return t(strings.planNoSlack, { target });
    }
    return tPlural(strings.planCanMiss, locale, plan.count, {
      count: format(plan.count),
      target,
    });
  }
  if (plan.kind === "mustAttend") {
    return tPlural(strings.planMustAttend, locale, plan.count, {
      count: format(plan.count),
      target,
    });
  }
  return t(strings.planUnreachable, { target });
}

export function planWorking(
  plan: AttendancePlan,
  attended: number,
  held: number,
  targetPercent: number,
  format: NumberFormatter,
  strings: AttendanceStrings
): string {
  const ratioValue = targetPercent / 100;
  const values = {
    attended: fmt(attended, format),
    held: fmt(held, format),
    ratio: fmt(ratioValue, format),
    target: fmt(targetPercent, format),
  };
  if (plan.kind === "canMiss") {
    return t(strings.workingCanMiss, {
      ...values,
      value: fmt(attended / ratioValue - held, format),
      count: format(plan.count),
    });
  }
  if (plan.kind === "mustAttend") {
    return t(strings.workingMustAttend, {
      ...values,
      value: fmt((ratioValue * held - attended) / (1 - ratioValue), format),
      count: format(plan.count),
    });
  }
  return t(strings.workingUnreachable, values);
}

function parseInputs(
  attended: string,
  held: string,
  target: string
): { attended: number; held: number; target: number } | null {
  const a = parseNumberInput(attended);
  const h = parseNumberInput(held);
  const goal = parseNumberInput(target);
  if (a === null || h === null || goal === null) {
    return null;
  }
  if (h <= 0 || a < 0 || a > h || goal <= 0 || goal > 100) {
    return null;
  }
  return { attended: a, held: h, target: goal };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const ATTENDANCE_CALCULATOR_STRINGS = {
  classesHeld: "Classes held",
  classesAttended: "Classes attended",
  requiredAttendance: "Required attendance %",
  attendance: "Attendance",
  classesMissed: "Classes missed",
  required: "Required",
  enterTheClassesHeldAt:
    "Enter the classes held (at least 1), classes attended (no more than the classes held), and a required percentage between 1 and 100 to see your results.",
  planNoSlack:
    "You are at or above {target}%, but you cannot afford to miss a single class — one more absence would drop you below the target.",
  planCanMiss:
    "one:You can miss {count} more class and still stay at or above {target}%.|other:You can miss {count} more classes and still stay at or above {target}%.",
  planMustAttend:
    "one:You must attend the next {count} class in a row to get back to {target}%.|other:You must attend the next {count} classes in a row to get back to {target}%.",
  planUnreachable:
    "A {target}% target is unreachable once you have missed a class — the ratio approaches 100% but never gets there.",
  workingCanMiss:
    "Missing m more classes must keep {attended} ÷ ({held} + m) ≥ {ratio}, so m ≤ {attended} ÷ {ratio} − {held} = {value} → rounded down = {count}",
  workingMustAttend:
    "Attending n classes in a row must give ({attended} + n) ÷ ({held} + n) ≥ {ratio}, so n ≥ ({ratio} × {held} − {attended}) ÷ (1 − {ratio}) = {value} → rounded up = {count}",
  workingUnreachable:
    "({attended} + n) ÷ ({held} + n) < 1 for every n, so a {target}% target can never be reached.",
} as const;

export default function AttendanceCalculatorWidget() {
  const [held, setHeld] = useState("80");
  const [attended, setAttended] = useState("62");
  const [target, setTarget] = useState("75");

  const inputs = parseInputs(attended, held, target);
  const percent = inputs ? (inputs.attended / inputs.held) * 100 : null;
  const plan = inputs
    ? planForTarget(inputs.attended, inputs.held, inputs.target)
    : null;

  const strings = useToolUi(ATTENDANCE_CALCULATOR_STRINGS);
  const formatNum = useToolNumber();
  const locale = useToolLocale();

  return (
    <>
      {/* Inputs */}
      <div className="flex flex-wrap items-end gap-6 rounded-lg border border-border bg-muted/30 p-5">
        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="classes-held"
          >
            {strings.classesHeld}
          </label>
          <input
            className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="classes-held"
            min={1}
            onChange={(e) => setHeld(e.target.value)}
            step="1"
            type="number"
            value={held}
          />
        </div>
        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="classes-attended"
          >
            {strings.classesAttended}
          </label>
          <input
            className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="classes-attended"
            min={0}
            onChange={(e) => setAttended(e.target.value)}
            step="1"
            type="number"
            value={attended}
          />
        </div>
        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="target-percent"
          >
            {strings.requiredAttendance}
          </label>
          <input
            className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="target-percent"
            max={100}
            min={1}
            onChange={(e) => setTarget(e.target.value)}
            step="0.1"
            type="number"
            value={target}
          />
        </div>
      </div>
      {/* Result */}
      {inputs && percent !== null && plan && (
        <section className="space-y-3">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              label={strings.attendance}
              value={`${formatNum(percent, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
            />
            <StatCard
              label={strings.classesMissed}
              value={fmt(inputs.held - inputs.attended, formatNum)}
            />
            <StatCard
              label={strings.required}
              value={`${fmt(inputs.target, formatNum)}%`}
            />
          </div>
          <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm leading-relaxed">
            ({fmt(inputs.attended, formatNum)} ÷ {fmt(inputs.held, formatNum)})
            × 100 ={" "}
            {formatNum(percent, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            %
          </p>
          <p className="rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
            {planMessage(plan, inputs.target, formatNum, strings, locale)}
          </p>
          <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm leading-relaxed">
            {planWorking(
              plan,
              inputs.attended,
              inputs.held,
              inputs.target,
              formatNum,
              strings
            )}
          </p>
        </section>
      )}
      {!inputs && (
        <p className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground text-sm leading-relaxed">
          {strings.enterTheClassesHeldAt}
        </p>
      )}
    </>
  );
}
